import { Action, State, StateContext, Store } from '@ngxs/store';
import { carriesAValue } from '../../../utils/an-answer-that-carries-its-value';
import { ActiveEventDetailOutputDto, EventsService, FindNextEventOfGroupsDto } from '@smatchy/sdk';

import { Injectable } from '@angular/core';
import { FindNextEventsOfMyGroupsAction, LoadAllNextEventsOfMyGroupsAction } from './events-of-my-groups.actions';
import { ShowToastErrorAction } from '../../app';
import { TranslateService } from '@ngx-translate/core';

export interface EventsOfMyGroupsStateModel {
  nextEventsOfMyGroups: ActiveEventDetailOutputDto[];
  loading: boolean;
  hasMoreData: boolean;
}

const defaults: EventsOfMyGroupsStateModel = {
  nextEventsOfMyGroups: [],
  loading: false,
  hasMoreData: true,
};

@State<EventsOfMyGroupsStateModel>({
  name: 'eventsOfMyGroups',
  defaults,
})
@Injectable()
export class EventsOfMyGroupsState {
  constructor(
    private readonly _eventsService: EventsService,
    private readonly _store: Store,
    private readonly _translate: TranslateService,
  ) {}

  @Action(LoadAllNextEventsOfMyGroupsAction)
  async loadNextEventsOfMyGroupsAction(
    { patchState, getState }: StateContext<EventsOfMyGroupsStateModel>,
    { payload }: LoadAllNextEventsOfMyGroupsAction,
  ) {
    this._eventsService
      .getNextOfMyGroups({
        getNextEventOfGroupsDto: {
          groupIds: payload.groupIds,
          count: 5,
          page: payload.page ?? 0,
        },
      })
      .subscribe((res) => {
        if (!carriesAValue(res)) {
          // Said "NOT IMPLEMENTED" in a system popup. The early return was
          // already the right behaviour; only the message was missing.
          this._store.dispatch(new ShowToastErrorAction(this._translate.instant('GROUPS.ERRORS.NEXT_EVENTS_FAILED')));
          return;
        }

        if (payload.page) {
          patchState({
            nextEventsOfMyGroups:
              payload.page === 0 ? res.value.events : [...getState().nextEventsOfMyGroups, ...res.value.events],
            hasMoreData: res.value.events.length >= 5,
          });
        } else {
          patchState({
            nextEventsOfMyGroups: res.value.events,
          });
        }
      });
  }

  @Action(FindNextEventsOfMyGroupsAction)
  async findNextEventsOfMyGroupsAction(
    { patchState }: StateContext<EventsOfMyGroupsStateModel>,
    { payload }: FindNextEventsOfMyGroupsAction,
  ) {
    this._eventsService
      .findNextEventOfGroupsDto({
        // Asserted, once, for the reason FindNextEventsOfMyGroupsAction gives:
        // the generated body requires a location the API treats as optional.
        findNextEventOfGroupsDto: {
          groupIds: payload.groupIds,
          count: 5,
          location: payload.location,
          page: 0,
        } as FindNextEventOfGroupsDto,
      })
      .subscribe({
        next: (res) => {
          if (!carriesAValue(res)) {
            console.error(res.error);
            return;
          }
          patchState({
            nextEventsOfMyGroups: res.value.events,
          });
        },
        // CAPACITOR-1R (401) and CAPACITOR-1J (status 0), 3 November 2026: with
        // no error callback, a refused or dropped call escaped as an unhandled
        // error and reached Sentry. Kept as quiet as the refusal above: this
        // block is a bonus on Explorer, the list already shown stays, and a 401
        // is the session's business, which the auth layer already handles.
        error: (error: unknown) => {
          console.error(error);
        },
      });
  }
}
